import React from "react";

const allowedFontFamilies = {
  masthead: "Bebas Neue, Impact, sans-serif",
  editorial: "Playfair Display, Georgia, serif",
  body: "Inter, -apple-system, sans-serif",
  mono: "DM Mono, ui-monospace, monospace",
  hand: "Caveat, cursive",
};

const allowedFits = new Set(["cover", "contain", "fill", "none", "scale-down"]);
const allowedAlign = new Set(["left", "center", "right", "justify"]);
const allowedWeights = new Set(["300", "400", "500", "600", "700", "800", "900", "normal", "bold"]);

const lengthPattern = /^-?\d+(\.\d+)?(px|rem|em|%|vh|vw|ch)?$/;
const colorPattern =
  /^(#[0-9a-f]{3,8}|rgba?\([0-9.,\s%]+\)|hsla?\([0-9.,\s%]+\)|var\(--[a-z0-9-]+\)|transparent|currentColor)$/i;
const positionPattern = /^[\w\s.%+-]+$/;

const textOr = (value, fallback = "") => {
  if (value === undefined || value === null) return fallback;
  const str = String(value);
  return str.length ? str : fallback;
};

const safeLength = (value) => {
  const str = String(value || "").trim();
  return lengthPattern.test(str) ? str : undefined;
};

const safeColor = (value) => {
  const str = String(value || "").trim();
  return colorPattern.test(str) ? str : undefined;
};

const safePosition = (value) => {
  const str = String(value || "").trim();
  return str && str.length <= 40 && positionPattern.test(str) ? str : undefined;
};

const safeLineHeight = (value) => {
  const str = String(value || "").trim();
  if (/^\d+(\.\d+)?$/.test(str)) return str;
  return safeLength(str);
};

const mergeSection = (fallback = {}, section = {}) => {
  const merged = {
    ...fallback,
    ...section,
    style: { ...(fallback.style || {}), ...(section.style || {}) },
    content: { ...(fallback.content || {}), ...(section.content || {}) },
  };

  [
    "sectionName",
    "title",
    "subtitle",
    "label",
    "eyebrow",
    "body",
    "caption",
    "imageUrl",
    "imageAlt",
    "imagePosition",
    "imageFit",
    "ctaLabel",
    "ctaUrl",
  ].forEach((key) => {
    merged[key] = textOr(section[key], fallback[key] || "");
  });

  merged.isVisible = section.isVisible !== false && fallback.isVisible !== false;
  merged.sortOrder = section.sortOrder ?? fallback.sortOrder ?? 0;
  return merged;
};

export const getSection = (sections, page, sectionKey, fallback = {}) => {
  const found = Array.isArray(sections)
    ? sections.find((section) => section.page === page && section.sectionKey === sectionKey)
    : null;

  if (found?.isVisible === false) return null;
  if (!found) return fallback?.isVisible === false ? null : fallback;
  return mergeSection(fallback, found);
};

export const sectionStyle = (section) => {
  const style = section?.style || {};
  const result = {};
  const fontFamily = style.fontFamily ? allowedFontFamilies[style.fontFamily] || style.fontFamily : "";

  if (safeLength(style.fontSize)) result.fontSize = safeLength(style.fontSize);
  if (fontFamily && /^[\w\s'",.-]+$/.test(fontFamily)) result.fontFamily = fontFamily;
  if (allowedWeights.has(String(style.fontWeight || ""))) result.fontWeight = String(style.fontWeight);
  if (allowedAlign.has(String(style.textAlign || ""))) result.textAlign = style.textAlign;
  if (safeLength(style.letterSpacing)) result.letterSpacing = safeLength(style.letterSpacing);
  if (safeLineHeight(style.lineHeight)) result.lineHeight = safeLineHeight(style.lineHeight);
  if (safeLength(style.marginTop)) result.marginTop = safeLength(style.marginTop);
  if (safeLength(style.marginBottom)) result.marginBottom = safeLength(style.marginBottom);
  if (safeLength(style.paddingTop)) result.paddingTop = safeLength(style.paddingTop);
  if (safeLength(style.paddingBottom)) result.paddingBottom = safeLength(style.paddingBottom);
  if (safeColor(style.backgroundColor)) result.backgroundColor = safeColor(style.backgroundColor);
  if (safeColor(style.textColor)) result.color = safeColor(style.textColor);
  if (safeLength(style.maxWidth)) result.maxWidth = safeLength(style.maxWidth);

  return result;
};

export const imageStyle = (section) => {
  const style = section?.style || {};
  const fit = textOr(style.imageFit, section?.imageFit || "cover");
  const position = textOr(style.imagePosition, section?.imagePosition || "center");
  const result = {
    objectFit: allowedFits.has(fit) ? fit : "cover",
    objectPosition: safePosition(position) || "center",
  };

  if (safeLength(style.imageWidth)) result.width = safeLength(style.imageWidth);
  if (safeLength(style.imageHeight)) result.height = safeLength(style.imageHeight);
  return result;
};

export const formattedText = (text) => {
  const normalized = String(text || "").replace(/\r\n/g, "\n");
  if (!normalized.trim()) return [];
  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.split("\n"))
    .filter((lines) => lines.some((line) => line.trim()));
};

export const renderMultilineText = (text) =>
  formattedText(text).map((lines, paragraphIndex) => (
    <React.Fragment key={`p-${paragraphIndex}`}>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={`${paragraphIndex}-${lineIndex}`}>
          {line}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
      {paragraphIndex < formattedText(text).length - 1 ? (
        <>
          <br />
          <br />
        </>
      ) : null}
    </React.Fragment>
  ));

